import React, { useState, useEffect } from 'react';
import { array, arrayOf, bool, func, number, object, shape, string } from 'prop-types';
import pickBy from 'lodash/pickBy';
import classNames from 'classnames';

import { useConfiguration } from '../../context/configurationContext';
import { useRouteConfiguration } from '../../context/routeConfigurationContext';
import { useSignInContext } from '../../context/SignInContext';
import { switchAppLanguage } from '../../util/genericHelpers';
import Icons from '../Icons/Icons';
import englishImage from '../LocaleMenu/flags/english.png';
import greekImage from '../LocaleMenu/flags/greek.png';
import germanImage from '../LocaleMenu/flags/german.png';

import { FormattedMessage, intlShape, useIntl } from '../../util/reactIntl';
import { isMainSearchTypeKeywords, isOriginInUse } from '../../util/search';
import { withViewport } from '../../util/uiHelpers';
import { parse, stringify, createSlug } from '../../util/urlHelpers';
import { createResourceLocatorString } from '../../util/routes';
import { propTypes } from '../../util/types';
import {
  isUsersStripeConnected,
  canAccessDashboard,
  isCorporateUser as isCorporateUserType,
  isIndividualUser,
  isClientUser,
} from '../../util/dataExtractors';
import routeConfiguration, { draftId, draftSlug } from '../../routing/routeConfiguration';

import {
  Button,
  LimitedAccessBanner,
  LinkedLogo,
  Modal,
  ModalMissingInformation,
  NamedLink,
  Menu,
  MenuLabel,
  MenuContent,
  MenuItem,
  InlineTextButton,
  IconAccordion,
  IconHeart,
  IconLogout,
  IconSettings,
  IconSessions,
  IconShield,
  IconWatched,
  IconSymptomsTracker,
  IconMoodsTracker,
  IconHabitsTracker,
} from '../../components';
import IconClose from '../IconClose/IconClose';

import MenuIcon from './MenuIcon';
import TopbarSearchForm from './TopbarSearchForm/TopbarSearchForm';
import GlobalSearch from './GlobalSearch/GlobalSearch';
import TopbarMobileMenu from './TopbarMobileMenu/TopbarMobileMenu';
import TopbarDesktop from './TopbarDesktop/TopbarDesktop';

import css from './Topbar.module.css';
import { trackLogOutClicked, trackMoodSymptomHabitEvent } from '../../util/mixpanelHelpers';
import { MIXPANEL_EVENTS } from '../../util/enums';

import SignInDrawer from './SignInDrawer/SignInDrawer';

const redirectToURLWithModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const searchString = `?${stringify({ [modalStateParam]: 'open', ...parse(search) })}`;
  history.push(`${pathname}${searchString}`, state);
};

const redirectToURLWithoutModalState = (props, modalStateParam) => {
  const { history, location } = props;
  const { pathname, search, state } = location;
  const queryParams = pickBy(parse(search), (v, k) => {
    return k !== modalStateParam;
  });
  const stringified = stringify(queryParams);
  const searchString = stringified ? `?${stringified}` : '';
  history.push(`${pathname}${searchString}`, state);
};

const GenericError = props => {
  const { show } = props;
  const classes = classNames(css.genericError, {
    [css.genericErrorVisible]: show,
  });
  return (
    <div className={classes}>
      <div className={css.genericErrorContent}>
        <p className={css.genericErrorText}>
          <FormattedMessage id="Topbar.genericError" />
        </p>
      </div>
    </div>
  );
};

GenericError.propTypes = {
  show: bool.isRequired,
};

const TopbarComponent = props => {
  const [mounted, setMounted] = useState(false);
  const [verificationEmailSent, setVerificationEmailSent] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isSubscriptionExpanded, setIsSubscriptionExpanded] = useState(false);
  const [isLocaleMenuOpen, setIsLocaleMenuOpen] = useState(false);

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
      setIsScrolled(scrollTop > 10);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const {
    className,
    rootClassName,
    desktopClassName,
    mobileRootClassName,
    mobileClassName,
    isAuthenticated,
    authScopes,
    authInProgress,
    currentUser,
    currentUserHasListings,
    currentUserHasOrders,
    currentPage,
    notificationCount,
    viewport,
    intl,
    location,
    onManageDisableScrolling,
    onResendVerificationEmail,
    sendVerificationEmailInProgress,
    sendVerificationEmailError,
    showGenericError,
    config,
    isHeaderSticky,
    isFullWidthHeader,
    headerWithoutShadow,
    staticPageHeader,
    ownListing,
    dashboardHeader,
    history,
    onLogout,
    logoVariant,
    events,
    onSubmitLogin,
    loginInProgress,
    loginError,
  } = props;

  const handleMobileMenuOpen = () => {
    redirectToURLWithModalState(props, 'mobilemenu');
  };

  const { openSignIn } = useSignInContext();

  const handleMobileMenuClose = () => {
    redirectToURLWithoutModalState(props, 'mobilemenu');
  };

  const handleMobileSearchClose = () => {
    redirectToURLWithoutModalState(props, 'mobilesearch');
  };

  const handleSubmit = values => {
    const { currentSearchParams } = props;
    const { routeConfiguration } = props;

    const topbarSearchParams = () => {
      if (isMainSearchTypeKeywords(config)) {
        return { keywords: values?.keywords };
      }
      // topbar search defaults to 'location' search
      const { search, selectedPlace } = values?.location;
      const { origin, bounds } = selectedPlace;
      const originMaybe = isOriginInUse(config) ? { origin } : {};

      return {
        ...originMaybe,
        address: search,
        bounds,
      };
    };
    const searchParams = {
      ...currentSearchParams,
      ...topbarSearchParams(),
    };
    history.push(createResourceLocatorString('SearchPage', routeConfiguration, {}, searchParams));
  };

  const handleLogout = () => {
    return onLogout();
  };

  const handleProfileMenuToggle = isOpen => {
    setIsProfileMenuOpen(isOpen);
  };

  const handleCloseProfileMenu = () => {
    setIsProfileMenuOpen(false);
  };

  const handleSubscriptionToggle = () => {
    setIsSubscriptionExpanded(!isSubscriptionExpanded);
  };

  const handleSubscriptionPageClick = () => {
    history.push(createResourceLocatorString('SubscriptionPage', routeConfiguration(), {}, {}));
  };

  const handleLanguageChange = locale => {
    switchAppLanguage({
      locale,
      pathname: location.pathname,
      search: location.search,
      hash: location.hash,
    });
  };

  const { mobilemenu, mobilesearch, keywords, address, origin, bounds } = parse(location.search, {
    latlng: ['origin'],
    latlngBounds: ['bounds'],
  });

  const notificationDot = notificationCount > 0 ? <div className={css.notificationDot} /> : null;

  const MAX_TAB_SCREEN_WIDTH = 1024;
  const isTabLayout = viewport.width < MAX_TAB_SCREEN_WIDTH;
  const isMobileLayout = viewport.width < MAX_TAB_SCREEN_WIDTH;

  const isMobileMenuOpen = isMobileLayout && mobilemenu === 'open';
  const isMobileSearchOpen = isMobileLayout && mobilesearch === 'open';
  const isTabMenuOpen = isTabLayout && mobilemenu === 'open';
  const isTabSearchOpen = isTabLayout && mobilesearch === 'open';

  const mobileMenu = (
    <TopbarMobileMenu
      isAuthenticated={isAuthenticated}
      currentUserHasListings={currentUserHasListings}
      currentUser={currentUser}
      ownListing={ownListing}
      onLogout={handleLogout}
      notificationCount={notificationCount}
      currentPage={currentPage}
      events={events}
      onSubmitLogin={onSubmitLogin}
      loginInProgress={loginInProgress}
      loginError={loginError}
      onClose={handleMobileMenuClose}
    />
  );

  const topbarSearcInitialValues = () => {
    if (isMainSearchTypeKeywords(config)) {
      return { keywords };
    }

    // Only render current search if full place object is available in the URL params
    const locationFieldsPresent = isOriginInUse(config)
      ? address && origin && bounds
      : address && bounds;
    return {
      location: locationFieldsPresent
        ? {
            search: address,
            selectedPlace: { address, origin, bounds },
          }
        : null,
    };
  };

  const initialSearchFormValues = topbarSearcInitialValues();

  const classes = classNames(rootClassName || css.root, className, {
    [css.staticPageTopbar]: staticPageHeader,
  });

  const isStripeConnected = isUsersStripeConnected(currentUser);
  const isCorporateUser = isCorporateUserType(currentUser);

  const authenticatedOnClientSide = mounted && isAuthenticated;
  const isAuthenticatedOrJustHydrated = isAuthenticated || !mounted;

  // Helper function for current page class
  const currentPageClass = page => {
    return currentPage === page ? css.currentPage : null;
  };

  const showEditApplication = ownListing?.attributes?.state === 'pendingApproval';

  const shouldShowOnboardingLink =
    isAuthenticated && (!isIndividualUser(currentUser) || showEditApplication) && !ownListing;

  const ownProfileLink = (
    <NamedLink
      className={classNames(css.yourListingsLink, currentPageClass('ownProfilePage'))}
      name="EditListingPage"
      params={{
        id: ownListing?.id?.uuid || draftId,
        slug: createSlug(ownListing?.attributes?.title || draftSlug),
        type: ownListing?.id?.uuid ? 'edit' : 'new',
        tab: 'details',
      }}
    >
      <FormattedMessage id="TopbarDesktop.completeProfile" />
    </NamedLink>
  );

  const isClient = isClientUser(currentUser);

  const startOnboardingLink = (
    <NamedLink
      className={classNames(css.yourListingsLink, currentPageClass('startOnboardingPage'))}
      name="DoctorOnboardingPage"
    >
      <FormattedMessage
        id={
          ownListing
            ? 'TopbarDesktop.editOnboardingApplication'
            : 'TopbarDesktop.startOnboardingApplication'
        }
      />
    </NamedLink>
  );

  const dashboardLink = isAuthenticatedOrJustHydrated && (
    <NamedLink
      className={classNames(css.yourListingsLink, currentPageClass('DashboardPage'))}
      name="DashboardPage"
    >
      <FormattedMessage id="TopbarDesktop.dashboardLink" />
    </NamedLink>
  );

  const profileMenu = authenticatedOnClientSide ? (
    <Menu isOpen={isProfileMenuOpen} onToggleActive={handleProfileMenuToggle}>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <div className={css.profileAvatar}>{currentUser?.attributes?.profile?.abbreviatedName}</div>
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="closeButton" className={css.closeButtonItem}>
          <div className={css.profileMenuHeader}>
            <div className={css.userInfoSection}>
              <div className={css.userAvatar}>
                {currentUser?.attributes?.profile?.abbreviatedName}
              </div>
              <div className={css.userDetails}>
                <div className={css.userName}>
                  {currentUser?.attributes?.profile?.firstName}{' '}
                  {currentUser?.attributes?.profile?.lastName}
                </div>
                <div className={css.userEmail}>{currentUser?.attributes?.email}</div>
              </div>
            </div>
            <button className={css.closeButton} onClick={handleCloseProfileMenu}>
              <IconClose />
            </button>
          </div>
        </MenuItem>
        {/* {isAuthenticated && currentUserHasListings && !isIndividualUser(currentUser) && (
          <MenuItem key="OwnProfilePage">{ownProfileLink}</MenuItem>
        )} */}

        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <div className={css.menuItemContent}>
              <IconSettings className={css.menuItemIcon} />
              <FormattedMessage id="TopbarDesktop.settingsLink" />
            </div>
          </NamedLink>
        </MenuItem>

        {!isCorporateUser && (
          <MenuItem key="SubscriptionPage" className={css.accordionItem}>
            <div className={css.accordionHeader}>
              <div className={css.menuItemContent}>
                <IconShield className={css.menuItemIcon} />
                <div className={css.yourListingsLink} onClick={handleSubscriptionPageClick}>
                  <FormattedMessage id="TopbarDesktop.mySubscriptionPage" />
                </div>
              </div>
              <div
                className={classNames(css.accordionIcon, {
                  [css.accordionIconExpanded]: isSubscriptionExpanded,
                })}
                onClick={handleSubscriptionToggle}
              >
                <IconAccordion />
              </div>
            </div>
            {isSubscriptionExpanded && (
              <div className={css.accordionContent}>
                <MenuItem key="SubscriptionPage" className={css.subMenuItem}>
                  <NamedLink
                    name="SubscriptionPage"
                    className={classNames(
                      css.yourListingsLink,
                      currentPageClass('SubscriptionPage')
                    )}
                  >
                    <FormattedMessage id="TopbarDesktop.subscriptionPage" />
                  </NamedLink>
                </MenuItem>
                <MenuItem key="MyInvoicesPage" className={css.subMenuItem}>
                  <NamedLink
                    name="MyInvoicesPage"
                    className={classNames(css.yourListingsLink, currentPageClass('MyInvoicesPage'))}
                  >
                    <FormattedMessage id="TopbarDesktop.myInvoicesPage" />
                  </NamedLink>
                </MenuItem>
              </div>
            )}
          </MenuItem>
        )}

        <MenuItem key="FavoritesPage">
          <NamedLink
            name="FavoritesPage"
            className={classNames(css.yourListingsLink, currentPageClass('FavoritesPage'))}
          >
            <div className={css.menuItemContent}>
              <IconHeart className={css.menuItemIcon} />
              <FormattedMessage id="TopbarDesktop.myFavorites" />
            </div>
          </NamedLink>
        </MenuItem>

        <MenuItem key="WatchedVideosPage">
          <NamedLink
            name="WatchedVideosPage"
            className={classNames(css.yourListingsLink, currentPageClass('WatchedVideosPage'))}
          >
            <div className={css.menuItemContent}>
              <IconWatched className={css.menuItemIcon} />
              <FormattedMessage id="TopbarDesktop.Watched" />
            </div>
          </NamedLink>
        </MenuItem>

        <MenuItem key="MyTrackPage">
          <div
            onClick={() => {
              history.push(
                createResourceLocatorString('MyTrackPage', routeConfiguration(), {}, {})
              );
              trackMoodSymptomHabitEvent(MIXPANEL_EVENTS?.HABIT_TRACKING_OPENED);
            }}
            className={classNames(css.yourListingsLink, currentPageClass('MyTrackPage'))}
          >
            <div className={css.menuItemContent}>
              <IconSymptomsTracker className={css.menuItemIcon} />
              <FormattedMessage id="Resource.habit.habitTrackTitle" />
            </div>
          </div>
        </MenuItem>

        <MenuItem key="logout">
          <InlineTextButton
            rootClassName={css.logoutButton}
            onClick={() => {
              trackLogOutClicked();
              onLogout();
            }}
          >
            <div className={css.menuItemContent}>
              <IconLogout className={css.menuItemIcon} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </div>
          </InlineTextButton>
        </MenuItem>

        <MenuItem key="divider" className={css.dividerItem}>
          <div className={css.divider}></div>
        </MenuItem>

        <MenuItem key="languageSection" className={css.languageSection}>
          <div className={css.languageButtons}>
            <div className={css.languageSectionHeader}>
              <FormattedMessage id="TopbarDesktop.languages" />
            </div>
            <button
              className={classNames(css.languageButton, {
                [css.languageButtonActive]: config.localization.locale === 'en',
              })}
              onClick={() => handleLanguageChange('en')}
            >
              <img src={englishImage} alt="English" className={css.languageFlag} />
              <span>EN</span>
            </button>
            <button
              className={classNames(css.languageButton, {
                [css.languageButtonActive]: config.localization.locale === 'el',
              })}
              onClick={() => handleLanguageChange('el')}
            >
              <img src={greekImage} alt="Greek" className={css.languageFlag} />
              <span>GR</span>
            </button>
            <button
              className={classNames(css.languageButton, {
                [css.languageButtonActive]: config.localization.locale === 'de',
              })}
              onClick={() => handleLanguageChange('de')}
            >
              <img src={germanImage} alt="German" className={css.languageFlag} />
              <span>DE</span>
            </button>
            <button
              className={classNames(css.languageButton, {
                [css.languageButtonActive]: config.localization.locale === 'ar',
              })}
              onClick={() => handleLanguageChange('ar')}
            >
              <span className={css.languageFlag}>
                <Icons name="globeIcon" />
              </span>
              <span>AR</span>
            </button>
          </div>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  const postApprorvalProfileMenu = authenticatedOnClientSide ? (
    <Menu isOpen={isProfileMenuOpen} onToggleActive={handleProfileMenuToggle}>
      <MenuLabel className={css.profileMenuLabel} isOpenClassName={css.profileMenuIsOpen}>
        <div className={css.profileAvatar}>{currentUser?.attributes?.profile?.abbreviatedName}</div>
      </MenuLabel>
      <MenuContent className={css.profileMenuContent}>
        <MenuItem key="closeButton" className={css.closeButtonItem}>
          <div className={css.profileMenuHeader}>
            <div className={css.userInfoSection}>
              <div className={css.userAvatar}>
                {currentUser?.attributes?.profile?.abbreviatedName}
              </div>
              <div className={css.userDetails}>
                <div className={css.userName}>
                  {currentUser?.attributes?.profile?.firstName}{' '}
                  {currentUser?.attributes?.profile?.lastName}
                </div>
                <div className={css.userEmail}>{currentUser?.attributes?.email}</div>
              </div>
            </div>
            <button className={css.closeButton} onClick={handleCloseProfileMenu}>
              <IconClose />
            </button>
          </div>
        </MenuItem>
        {/* {isAuthenticated && currentUserHasListings && !isIndividualUser(currentUser) && (
          <MenuItem key="OwnProfilePage">{ownProfileLink}</MenuItem>
        )} */}

        {shouldShowOnboardingLink && !ownListing && (
          <MenuItem key="StartOnboardingPage">{startOnboardingLink}</MenuItem>
        )}

        {canAccessDashboard(currentUser) && (
          <MenuItem key="DashboardPage">{dashboardLink}</MenuItem>
        )}

        <MenuItem key="ManageListingsPage">
          <div className={css.profileMenuHead}></div>
        </MenuItem>

        <MenuItem key="EditListingPage">
          {ownListing ? (
            <NamedLink
              name="EditListingPage"
              params={{
                id: ownListing?.id?.uuid || draftId,
                slug: createSlug(ownListing?.attributes?.title || draftSlug),
                type: ownListing ? 'edit' : 'new',
                tab: 'details',
              }}
              className={classNames(css.yourListingsLink, currentPageClass('EditListingPage'))}
            >
              <FormattedMessage id="TopbarDesktop.editProfile" />
            </NamedLink>
          ) : (
            <NamedLink
              name="DoctorOnboardingPage"
              className={classNames(css.yourListingsLink, currentPageClass('DoctorOnboardingPage'))}
            >
              <FormattedMessage id="TopbarDesktop.completeProfile" />
            </NamedLink>
          )}
        </MenuItem>

        <MenuItem key="InboxPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('InboxPage'))}
            name="InboxPage"
            params={{ tab: currentUserHasListings ? 'sales' : 'orders' }}
          >
            <div className={css.menuItemContent}>
              <IconSessions className={css.menuItemIcon} />
              <FormattedMessage id="TopbarDesktop.inbox" />
            </div>
          </NamedLink>
        </MenuItem>

        <MenuItem key="AccountSettingsPage">
          <NamedLink
            className={classNames(css.yourListingsLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <div className={css.menuItemContent}>
              <IconSettings className={css.menuItemIcon} />
              <FormattedMessage id="TopbarDesktop.accountSettingsLink" />
            </div>
          </NamedLink>
        </MenuItem>

        <MenuItem key="logout">
          <InlineTextButton
            rootClassName={css.logoutButton}
            onClick={() => {
              trackLogOutClicked();
              onLogout();
            }}
          >
            <div className={css.menuItemContent}>
              <IconLogout className={css.menuItemIcon} />
              <FormattedMessage id="TopbarDesktop.logout" />
            </div>
          </InlineTextButton>
        </MenuItem>

        <MenuItem key="divider" className={css.dividerItem}>
          <div className={css.divider}></div>
        </MenuItem>

        <MenuItem key="languageSection" className={css.languageSection}>
          <div className={css.languageSectionHeader}>
            <FormattedMessage id="TopbarDesktop.languages" />
          </div>
          <div className={css.languageButtons}>
            <button
              className={classNames(css.languageButton, {
                [css.languageButtonActive]: config.localization.locale === 'en',
              })}
              onClick={() => handleLanguageChange('en')}
            >
              <img src={englishImage} alt="English" className={css.languageFlag} />
              <span>EN</span>
            </button>
            <button
              className={classNames(css.languageButton, {
                [css.languageButtonActive]: config.localization.locale === 'el',
              })}
              onClick={() => handleLanguageChange('el')}
            >
              <img src={greekImage} alt="Greek" className={css.languageFlag} />
              <span>GR</span>
            </button>
            <button
              className={classNames(css.languageButton, {
                [css.languageButtonActive]: config.localization.locale === 'de',
              })}
              onClick={() => handleLanguageChange('de')}
            >
              <img src={germanImage} alt="German" className={css.languageFlag} />
              <span>DE</span>
            </button>
            <button
              className={classNames(css.languageButton, {
                [css.languageButtonActive]: config.localization.locale === 'ar',
              })}
              onClick={() => handleLanguageChange('ar')}
            >
              <span className={css.languageFlag}>
                <Icons name="globeIcon" />
              </span>
              <span>AR</span>
            </button>
          </div>
        </MenuItem>
      </MenuContent>
    </Menu>
  ) : null;

  const renderExtraLinks = <>{isStripeConnected ? postApprorvalProfileMenu : profileMenu}</>;
  const mobileSignInButton = !isAuthenticated ? (
    <button
      type="button"
      className={css.mobileSignInButton}
      onClick={() => openSignIn({ defaultToLogin: true, fullScreenOnMobile: true })}
    >
      <FormattedMessage id="Topbar.signIn" />
    </button>
  ) : null;

  return (
    <div className={classes}>
      <LimitedAccessBanner
        isAuthenticated={isAuthenticated}
        authScopes={authScopes}
        currentUser={currentUser}
        onLogout={handleLogout}
        currentPage={currentPage}
      />
      <div className={classNames(mobileRootClassName || css.container, mobileClassName)}>
        <Button
          rootClassName={css.menu}
          onClick={handleMobileMenuOpen}
          title={intl.formatMessage({ id: 'Topbar.menuIcon' })}
        >
          <MenuIcon className={css.menuIcon} />
          {notificationDot}
        </Button>

        <LinkedLogo
          isAuthenticated={isAuthenticated}
          format={'mobile'}
          alt={intl.formatMessage({ id: 'Topbar.logoIcon' })}
          logoVariant={logoVariant}
        />
        <div className={css.mobileEndControl}>
          {isAuthenticated ? renderExtraLinks : mobileSignInButton}
        </div>
      </div>
      <div className={css.desktop}>
        <TopbarDesktop
          className={desktopClassName}
          currentUserHasListings={currentUserHasListings}
          currentUser={currentUser}
          currentPage={currentPage}
          initialSearchFormValues={initialSearchFormValues}
          intl={intl}
          isAuthenticated={isAuthenticated}
          notificationCount={notificationCount}
          onLogout={handleLogout}
          ownListing={ownListing}
          onSearchSubmit={handleSubmit}
          appConfig={config}
          viewport={viewport}
          isHeaderSticky={isHeaderSticky}
          staticPageHeader={staticPageHeader}
          dashboardHeader={dashboardHeader}
          isFullWidthHeader={isFullWidthHeader}
          headerWithoutShadow={headerWithoutShadow}
          events={events}
          onSubmitLogin={props.onSubmitLogin}
          loginInProgress={props.loginInProgress}
          loginError={props.loginError}
        />
      </div>
      <Modal
        id="TopbarMobileMenu"
        isOpen={staticPageHeader ? isTabMenuOpen : isMobileMenuOpen}
        onClose={handleMobileMenuClose}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
        ismobileMenuModal={true}
      >
        {authInProgress ? null : mobileMenu}
      </Modal>
      <Modal
        id="TopbarMobileSearch"
        containerClassName={css.modalContainer}
        isOpen={staticPageHeader ? isTabSearchOpen : isMobileSearchOpen}
        onClose={handleMobileSearchClose}
        usePortal
        onManageDisableScrolling={onManageDisableScrolling}
      >
        <div className={css.searchContainer}>
          <GlobalSearch isExpanded requiresAuthToExpand={false} />
          <p className={css.mobileHelp}>
            <FormattedMessage id="Topbar.mobileSearchHelp" />
          </p>
        </div>
      </Modal>
      <ModalMissingInformation
        id="MissingInformationReminder"
        containerClassName={css.missingInformationModal}
        currentUser={currentUser}
        currentUserHasListings={currentUserHasListings}
        currentUserHasOrders={currentUserHasOrders}
        location={location}
        isAuthenticated={isAuthenticated}
        onManageDisableScrolling={onManageDisableScrolling}
        onResendVerificationEmail={onResendVerificationEmail}
        sendVerificationEmailInProgress={sendVerificationEmailInProgress}
        sendVerificationEmailError={sendVerificationEmailError}
      />

      <GenericError show={showGenericError} />
    </div>
  );
};

TopbarComponent.defaultProps = {
  className: null,
  rootClassName: null,
  desktopClassName: null,
  mobileRootClassName: null,
  mobileClassName: null,
  notificationCount: 0,
  currentUser: null,
  currentUserHasOrders: null,
  currentPage: null,
  sendVerificationEmailError: null,
  authScopes: [],
  logoVariant: null,
};

TopbarComponent.propTypes = {
  className: string,
  rootClassName: string,
  desktopClassName: string,
  mobileRootClassName: string,
  mobileClassName: string,
  isAuthenticated: bool.isRequired,
  authScopes: array,
  authInProgress: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentUserHasListings: bool.isRequired,
  currentUserHasOrders: bool,
  currentPage: string,
  notificationCount: number,
  onLogout: func.isRequired,
  onManageDisableScrolling: func.isRequired,
  onResendVerificationEmail: func.isRequired,
  sendVerificationEmailInProgress: bool.isRequired,
  sendVerificationEmailError: propTypes.error,
  showGenericError: bool.isRequired,
  logoVariant: string,
  events: array,

  // These are passed from Page to keep Topbar rendering aware of location changes
  history: shape({
    push: func.isRequired,
  }).isRequired,
  location: shape({
    search: string.isRequired,
  }).isRequired,

  // from withViewport
  viewport: shape({
    width: number.isRequired,
    height: number.isRequired,
  }).isRequired,

  // from useIntl
  intl: intlShape.isRequired,

  // from useConfiguration
  config: object.isRequired,

  // from useRouteConfiguration
  routeConfiguration: arrayOf(propTypes.route).isRequired,
};

const EnhancedTopbar = props => {
  const config = useConfiguration();
  const routeConfiguration = useRouteConfiguration();
  const intl = useIntl();
  return (
    <TopbarComponent
      config={config}
      routeConfiguration={routeConfiguration}
      intl={intl}
      {...props}
    />
  );
};

const Topbar = withViewport(EnhancedTopbar);
Topbar.displayName = 'Topbar';

export default Topbar;
